import React, { cloneElement, useEffect } from 'react';
import PropTypes from 'prop-types';
import NewsModal from '../../PublicPages/HomePage/NewsDetail/NewsItem/NewsModal/NewsModal.jsx';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Alert } from 'antd';
import { useState } from 'react';
import LanguageBlock from './LanguageBlock';
import useFormDisabledStates from '../../../../hooks/useFormDisabledStates';
import { AT_LEAST_ONE_LANGUAGE_VERSION_MESSAGE } from '../../../../utils/constants/common_constants.js';
import { formIsEmpty, clearFieldErrors } from '../../../../utils/helpers/validation';
import {
  checkIfAltAttributesMissing,
  addAltAttributestoFormContent,
} from '../../../../utils/helpers/imageAltValidator';
import {
  INSIGHT_SK,
  INSIGHT_EN,
} from '../../../../utils/constants/insight_constants.js';
import { useToastMessages } from '../../../../hooks/useToastMessages';
import FormButtonsContainer from '../../../antd/form/FormButtonsContainer/FormButtonsContainer';
import FormButton from '../../../antd/form/FormButton/FormButton';
import Form from '../../../antd/form/Form/Form';
import QuestionModal from '../../../common/modals/QuestionModal/QuestionModal';
import { MainButton, ButtonContainer } from '../../../common/form/FormElements';

const FormWrapper = ({
  initials,
  creator,
  onSubmit,
  directory,
  visibleElement,
  setVisibleElement,
  form,
  children,
  setFormDisabledStates,
}) => {
  const { t } = useTranslation();
  const [showAltModal, setShowAltModal] = useState(false);
  const [pendingSubmitValues, setPendingSubmitValues] = useState(null);
  const altSourceTypeLabel =
    directory === 'articles' ? t('článku') : t('aktuality');
  const { disabledStates, updateDisabledState } = useFormDisabledStates(
    form,
    initials,
  );

  const { apiErrorMessage: showErrorMessage } = useToastMessages();

  useEffect(() => {
    setFormDisabledStates(disabledStates);
  }, [disabledStates]);

  const areAllLanguageBlockEmpty = () => {
    const allLanguageBlocksEmpty =
      formIsEmpty(form, 'sk') && formIsEmpty(form, 'en');

    if (allLanguageBlocksEmpty) {
      setTimeout(() => {
        const firstErrorElement = document.getElementsByClassName(
          'ant-form-item-explain-error',
        )[0];
        if (firstErrorElement) {
          const ancestor = findAncestor(firstErrorElement, 'ant-row');
          ancestor.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'center',
          });
        }
      }, 100);

      showErrorMessage(t(AT_LEAST_ONE_LANGUAGE_VERSION_MESSAGE));
      return true;
    }

    return false;
  };

  // Find the first ancestor with the class name
  function findAncestor(el, cls) {
    while ((el = el.parentElement) && !el.classList.contains(cls)) {
      // keep walking up the DOM tree until the requested ancestor is found
    }
    return el;
  }

  const hasMeaningfulText = (value) => {
    if (value === null || value === undefined) return false;
    if (value === 'null' || value === 'undefined') return false;
    if (typeof value !== 'string') return Boolean(value);

    const normalized = value.trim();
    if (!normalized) return false;

    const textarea = document.createElement('textarea');
    textarea.innerHTML = normalized;
    const decoded = textarea.value;

    const withoutHtml = decoded
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/[\u00A0\u200B\u200C\u200D\uFEFF]/g, '');

    return withoutHtml.replace(/\s+/g, '') !== '';
  };

  const validateArticleLanguageRules = (values) => {
    if (directory !== 'articles') {
      return false;
    }

    clearFieldErrors(form, [
      'title_sk',
      'content_sk',
      'title_en',
      'content_en',
    ]);

    const hasTitleSk = hasMeaningfulText(values.title_sk);
    const hasContentSk = hasMeaningfulText(values.content_sk);
    const hasTitleEn = hasMeaningfulText(values.title_en);
    const hasContentEn = hasMeaningfulText(values.content_en);

    const skFilled = hasTitleSk || hasContentSk;
    const enFilled = hasTitleEn || hasContentEn;

    const fieldsWithErrors = [];

    if (skFilled && !hasTitleSk) {
      fieldsWithErrors.push({
        name: 'title_sk',
        errors: [
          t('V slovenskej verzii musia byť vyplnené oba polia: Názov aj Obsah.'),
        ],
      });
      showErrorMessage(
        t('V slovenskej verzii musia byť vyplnené oba polia: Názov aj Obsah.'),
      );
    }

    if (skFilled && !hasContentSk) {
      fieldsWithErrors.push({
        name: 'content_sk',
        errors: [
          t('V slovenskej verzii musia byť vyplnené oba polia: Názov aj Obsah.'),
        ],
      });
      showErrorMessage(
        t('V slovenskej verzii musia byť vyplnené oba polia: Názov aj Obsah.'),
      );
    }

    if (enFilled && !hasTitleEn) {
      fieldsWithErrors.push({
        name: 'title_en',
        errors: [
          t('V anglickej verzii musia byť vyplnené oba polia: Názov aj Obsah.'),
        ],
      });
      showErrorMessage(
        t('V anglickej verzii musia byť vyplnené oba polia: Názov aj Obsah.'),
      );
    }

    if (enFilled && !hasContentEn) {
      fieldsWithErrors.push({
        name: 'content_en',
        errors: [
          t('V anglickej verzii musia byť vyplnené oba polia: Názov aj Obsah.'),
        ],
      });
      showErrorMessage(
        t('V anglickej verzii musia byť vyplnené oba polia: Názov aj Obsah.'),
      );
    }


    if (fieldsWithErrors.length > 0) {
      form.setFields(fieldsWithErrors);
      return true;
    }

    return false;
  };

  return (
    <Form
      form={form}
      validateTrigger="onSubmit"
      initialValues={initials}
      onFinish={(values) => {
        if (areAllLanguageBlockEmpty()) {
          return;
        }

        if (validateArticleLanguageRules(values)) {
          return;
        }

        // Check if images are missing alt attributes
        if (checkIfAltAttributesMissing(values)) {
          setPendingSubmitValues(values);
          setShowAltModal(true);
          return;
        }

        onSubmit({
          ...values,
          //user_id: values.user_id || user.person_id, // this is added here because some pages do not have user id (PH no such news)
          id: initials.id,
        });
      }}
      layout="vertical"
      style={{ margin: '10px' }}
      scrollToFirstError={true}
    >
      {/* Purpose: render children components and if the component is lanugage than confiugre it */}
      {React.Children.map(children, (child) =>
        child?.type === LanguageBlock
          ? cloneElement(child, {
              onChange: updateDisabledState,
              formIsEmpty: formIsEmpty(form, child.props.language),
              formIsFilled: !disabledStates[child.props.language],
              directory: directory,
              form: form,
            })
          : child,
      )}

      <NewsModal
        visible={visibleElement === INSIGHT_SK || visibleElement === INSIGHT_EN}
        toggleModal={() => setVisibleElement(undefined)}
        content={
          (visibleElement === INSIGHT_SK && form.getFieldValue('content_sk')) ||
          form.getFieldValue('content_en')
        }
        title={
          (visibleElement === INSIGHT_SK && form.getFieldValue('title_sk')) ||
          form.getFieldValue('title_en')
        }
        dateOfCreate={dayjs(new Date()).format('D.MM.YYYY h:mm')}
        creatorName={creator}
      />
      <Alert
        showIcon
        type="warning"
        title={
          <>
            <span style={{ color: 'red' }}>** </span>
            {t(AT_LEAST_ONE_LANGUAGE_VERSION_MESSAGE)}
          </>
        }
      />
      <FormButtonsContainer>
        <FormButton
          style={{ marginTop: '15px' }}
          submitOnClick={true}
          submitKey={directory}
        >
          {t('Uložiť')}
        </FormButton>
      </FormButtonsContainer>

      <QuestionModal
        visible={showAltModal}
        toggle={() => {
          setShowAltModal(false);
          setPendingSubmitValues(null);
        }}
        headerTitle={t('Obrázkom chýbajú textové popisy')}
        question={t(
          `V obsahu sú obrázky bez textových popisov (alt atribút). Chcete ich doplniť z názvu ${altSourceTypeLabel}?`,
        )}
        showDefaultButtons={false}
        customButtons={
          <ButtonContainer>
            <MainButton
              onClick={() => {
                setShowAltModal(false);
                setPendingSubmitValues(null);
              }}
            >
              {t('Nie, doplním sám')}
            </MainButton>
            <MainButton
              onClick={() => {
                const updatedValues = addAltAttributestoFormContent(
                  pendingSubmitValues,
                  pendingSubmitValues.title_sk || '',
                  pendingSubmitValues.title_en || ''
                );

                onSubmit({
                  ...updatedValues,
                  id: initials.id,
                });
                setShowAltModal(false);
                setPendingSubmitValues(null);
              }}
            >
              {t('Áno, doplniť a uložiť')}
            </MainButton>
          </ButtonContainer>
        }
      />
    </Form>
  );
};

FormWrapper.propTypes = {
  initials: PropTypes.object,
  creator: PropTypes.string,
  onSubmit: PropTypes.func,
  directory: PropTypes.string,
  children: PropTypes.node,
  visibleElement: PropTypes.any,
  setVisibleElement: PropTypes.func,
  form: PropTypes.object,
};

export default FormWrapper;
